<script setup lang="ts">
import CopyButton from '@/ui-plus/CopyButton.vue';
import { currentWebsite } from '@partner-spa-vue/stores/MyWebsitesStore';
import { ref, watch, computed } from 'vue';
import { dashboardApiClient } from '@api-client/dashboard';
import { currentWebsiteId } from '@partner-spa-vue/stores/MyWebsitesStore';
import {
    currentSupportStripeStatus,
    loadCurrentSupportStripeStatus
} from '@partner-spa-vue/stores/CurrentWebsiteStripeStatusStore';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { toast } from '@/ui-plus/sonner';
import { darkModeAppearanceVariables, lightModeAppearanceVariables } from '@lib/clientUtils/stripeColors';
import { useColorMode } from "@vueuse/core";
import type { ResType } from '@api-client/typeUtils';
import { Button } from '@/components/ui/button';

const stripeAccountSession = ref<ResType<typeof dashboardApiClient.website.partner_support.stripe_account_session[':websiteId']['$get']>["data"] | null>(null);
const isLoading = ref(false);
let stripeConnectInstance: ReturnType<typeof loadConnectAndInitialize> | null = null;

const mode = useColorMode();
const isDarkMode = computed(() => (mode.value === "dark" ? true : false));
watch(isDarkMode, () => {
    if (stripeConnectInstance) {
        stripeConnectInstance.update({
            appearance: {
                variables: isDarkMode.value ? darkModeAppearanceVariables : lightModeAppearanceVariables,
            },
        });
    }
});

const isOnboardingComplete = computed(() => {
    if (stripeAccountSession.value) {
        return stripeAccountSession.value.account?.charges_enabled && stripeAccountSession.value.account?.details_submitted;
    }
    if (currentSupportStripeStatus.value?.account) {
        return currentSupportStripeStatus.value.account.charges_enabled && currentSupportStripeStatus.value.account.details_submitted;
    }
    return false;
});

const loadClientSecret = async () => {
    loadCurrentSupportStripeStatus();
    isLoading.value = true;
    const { data, error } = await (await dashboardApiClient.website.partner_support.stripe_account_session[':websiteId'].$get({
        param: {
            websiteId: currentWebsiteId.value || '',
        },
    })).json();
    isLoading.value = false;
    await new Promise(resolve => setTimeout(resolve, 500));
    if (error) {
        console.error(error);
        toast.error(error.message || 'Failed to load Stripe account session');
    }
    if (data) {
        stripeAccountSession.value = data;
        const container = document.getElementById("stripe-connect-container");
        // remove all inside of container
        while (container?.firstChild) {
            container.removeChild(container.firstChild);
        }
        if (container) {
            stripeConnectInstance = loadConnectAndInitialize({
                publishableKey: import.meta.env.PUBLIC_STRIPE_PUBLISHABLE_KEY,
                fetchClientSecret: () => Promise.resolve(data.client_secret || ''),
                appearance: {
                    variables: {
                        colorPrimary: "hsl(142.1, 76.2%, 36.3%)"
                    },
                },
            });
            if (data.type === 'account_onboarding') {
                const accountOnboarding = stripeConnectInstance.create('account-onboarding');
                accountOnboarding.setOnExit(() => {
                    loadClientSecret();
                });
                container.appendChild(accountOnboarding);
            } else if (data.type === 'account_management') {
                const accountManagement = stripeConnectInstance.create('account-management');
                container.appendChild(accountManagement);
            } else {
                toast.error('Failed to load Stripe account session');
            }
        }
    }
}

const isStripeWidgetStarted = ref(false);
function startStripeWidget() {
    isStripeWidgetStarted.value = true;
    setTimeout(() => {
        loadClientSecret();
    });
}
</script>

<template>
    Website for stripe:
    <CopyButton v-if="currentWebsite?.defaultHostname"
                class="mb-4"
                :content="`https://${currentWebsite?.defaultHostname}`">
        <span class="text-sm">
            {{ `https://${currentWebsite?.defaultHostname}` }}
        </span>
    </CopyButton>

    <br>
    
    Product description for stripe: 
    <CopyButton 
                class="mb-4"
                :content="`online ordering`">
        <span class="text-sm">
            {{ `online ordering` }}
        </span>
    </CopyButton>

    <br>

    <div v-if="currentSupportStripeStatus?.alerts?.length"
         class="mt-4 space-y-2">
        <p class="font-medium text-sm">Actions required</p>
        <div v-for="(alert, i) in currentSupportStripeStatus.alerts"
             :key="i"
             class="rounded-md border p-3 text-sm"
             :class="{
                'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100': alert.type === 'info',
                'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-100': alert.type === 'warning',
                'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100': alert.type === 'error' || alert.type === 'critical',
            }">
            <p class="font-medium">{{ alert.title }}</p>
            <p v-if="alert.reason"
               class="mt-1 text-xs opacity-90">{{ alert.reason }}</p>
            <p v-if="alert.deadline"
               class="mt-1 text-xs opacity-90">
                Deadline: {{ new Date(alert.deadline * 1000).toLocaleString() }}
            </p>
            <ul v-if="alert.fields?.length"
                class="mt-1 list-disc pl-4 text-xs opacity-90">
                <li v-for="field in alert.fields"
                    :key="field">{{ field }}</li>
            </ul>
        </div>
    </div>
    <Button @click="startStripeWidget"
            v-if="!isStripeWidgetStarted"
            class="w-full mt-4">
        Click here to start Stripe
    </Button>
    <div v-if="isStripeWidgetStarted"
         class="space-y-6 border-border border-2 p-2 rounded-md">
        <div v-if="!isLoading">
            <Button @click="loadClientSecret"
                    variant="outline">Refresh</Button>
            ({{ (isOnboardingComplete ? '✅ Stripe Account Active' : '❌ Stripe Account Not Active') }})
            <Button as="a"
                    v-if="isOnboardingComplete && stripeAccountSession && stripeAccountSession.account && stripeAccountSession.account.id"
                    :href="`https://dashboard.stripe.com/${stripeAccountSession.account.id}`"
                    target="_blank"
                    class="w-full md:w-auto"
                    variant="outline">
                Open Stripe Dashboard
            </Button>
            {{ stripeAccountSession?.account?.id }}
            {{ stripeAccountSession?.account?.email }}
        </div>
        <div v-if="isLoading">Loading...</div>
        <div id="stripe-connect-container"
             v-else></div>
    </div>
</template>